import { CommonModule } from "@angular/common";
import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { TabsModule } from "primeng/tabs";
import { TableModule } from "primeng/table";
import { SkeletonModule } from "primeng/skeleton";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { DialogHeaderComponent } from "../../../../shared/components/dialog-header/dialog-header.component";
import { DateTime } from "luxon";
import { finalize, forkJoin, Subject, takeUntil } from "rxjs";
import { MarketingApiService } from "../../../services/marketing/marketing-api.service";
import {
  isMarketingProvider,
  MarketingBinding,
  MarketingIntegration,
  MarketingProvider,
  MarketingReport,
} from "../../../services/marketing/marketing.models";
import { EventRegistrationEditorComponent } from "./event-registration-editor.component";

@Component({
  selector: "app-marketing-panel",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TabsModule,
    TableModule,
    SkeletonModule,
    MatDialogModule,
    ButtonComponent,
    DialogHeaderComponent,
  ],
  templateUrl: "./marketing-panel.component.html",
  styleUrl: "./marketing-panel.component.scss",
})
export class MarketingPanelComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) eventId!: number;
  @Input() eventName = "";
  @Input() hideMoney = false;
  @Input() readOnly = false;
  private readonly api = inject(MarketingApiService);
  private readonly dialog = inject(MatDialog);
  @ViewChild("integrationSettings", { static: true })
  private settingsDialog!: TemplateRef<unknown>;
  private settingsDialogRef?: MatDialogRef<unknown>;
  private editorDialogRef?: MatDialogRef<EventRegistrationEditorComponent>;
  managingProvider: MarketingProvider | null = null;
  integrationsLoaded = false;
  private readonly destroy$ = new Subject<void>();
  private readonly eventChanged$ = new Subject<void>();
  private readonly requestChanged$ = new Subject<void>();
  private reportQuery: Record<string, string> | null = null;
  tab: "results" | "integrations" = "results";
  report: MarketingReport | null = null;
  integrations: MarketingIntegration[] = [];
  bindings: MarketingBinding[] = [];
  busy = false;
  error = "";
  notice = "";
  page = 1;
  orderPage = 1;
  filterOptions: Record<"source" | "medium" | "campaign", string[]> = {
    source: [],
    medium: [],
    campaign: [],
  };
  filters = {
    start_date:
      DateTime.now()
        .setZone("America/Sao_Paulo")
        .minus({ days: 30 })
        .toISODate() || "",
    end_date:
      DateTime.now()
        .setZone("America/Sao_Paulo")
        .plus({ days: 1 })
        .toISODate() || "",
    source: "",
    medium: "",
    campaign: "",
    sale_channel: "online",
    payment_method: "",
  };
  readonly providers: {
    key: MarketingProvider;
    name: string;
    description: string;
    logo: string;
  }[] = [
    {
      key: "meta",
      name: "Meta Ads",
      description: "Facebook e Instagram",
      logo: "assets/marketing/social-media/meta.svg",
    },
    {
      key: "google",
      name: "Google Ads",
      description: "",
      logo: "assets/marketing/social-media/google-ads.png",
    },
  ];
  readonly journeyEvents = [
    { key: "event_viewed", label: "Visualização do evento" },
    { key: "cart_item_added", label: "Adição ao carrinho" },
    { key: "checkout_started", label: "Início de checkout" },
    { key: "purchase_confirmed", label: "Compra confirmada" },
  ];
  readonly reasonLabels: Record<string, string> = {
    direct: "Direto",
    consent_denied: "Mensuração recusada",
    consent_unknown: "Preferência desconhecida",
    context_missing: "Contexto ausente",
    context_invalid: "Contexto inválido",
    outside_window: "Fora da janela",
    historical: "Histórico anterior à captura",
    unsupported_channel: "Canal sem atribuição online",
  };
  selected: Partial<Record<MarketingProvider, number>> = {};
  draftBindings: Partial<Record<MarketingProvider, MarketingBinding>> = {};

  ngOnChanges() {
    this.editorDialogRef?.close();
    this.eventChanged$.next();
    this.closeSettings();
    this.integrationsLoaded = false;
    this.integrations = [];
    this.bindings = [];
    this.selected = {};
    this.draftBindings = {};
    this.error = "";
    this.notice = "";
    this.page = 1;
    this.orderPage = 1;
    this.reportQuery = null;
    this.report = null;
    this.filterOptions = { source: [], medium: [], campaign: [] };
    this.filters.source = "";
    this.filters.medium = "";
    this.filters.campaign = "";
    this.loadTab();
  }
  ngOnDestroy() {
    this.editorDialogRef?.close();
    this.closeSettings();
    this.destroy$.next();
    this.destroy$.complete();
    this.eventChanged$.complete();
    this.requestChanged$.complete();
  }
  selectTab(tab: typeof this.tab) {
    this.requestChanged$.next();
    this.tab = tab;
    this.error = "";
    this.notice = "";
    this.loadTab();
  }
  changeTab(value: string | number | undefined) {
    if (
      (value === "results" || value === "integrations") &&
      value !== this.tab
    ) {
      this.selectTab(value);
    }
  }
  private loadTab() {
    if (this.tab === "results") {
      this.loadReport();
    } else this.loadIntegrations();
  }
  query(): Record<string, string> {
    return Object.fromEntries(
      Object.entries({
        ...this.filters,
        timezone: "America/Sao_Paulo",
        page: String(this.page),
        order_page: String(this.orderPage),
        per_page: "50",
      })
        .map(([key, value]) => [key, value.trim()])
        .filter(([, value]) => value !== ""),
    );
  }
  filter() {
    this.page = 1;
    this.orderPage = 1;
    this.loadReport();
  }
  loadReport(query = this.query()) {
    if (!this.eventId) return;
    this.requestChanged$.next();
    const start = DateTime.fromISO(query["start_date"] || "", {
      zone: "America/Sao_Paulo",
    });
    const end = DateTime.fromISO(query["end_date"] || "", {
      zone: "America/Sao_Paulo",
    });
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(query["start_date"] || "") ||
      !/^\d{4}-\d{2}-\d{2}$/.test(query["end_date"] || "") ||
      !start.isValid ||
      !end.isValid ||
      end <= start ||
      end.diff(start, "days").days > 366
    ) {
      this.error =
        "Confira o período: datas válidas, início anterior ao fim e no máximo 366 dias.";
      this.report = null;
      this.reportQuery = null;
      return;
    }
    this.busy = true;
    this.error = "";
    this.report = null;
    this.api
      .report(this.eventId, query)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.eventChanged$),
        takeUntil(this.requestChanged$),
        finalize(() => (this.busy = false)),
      )
      .subscribe({
        next: (report) => {
          this.report = report;
          this.filterOptions = report.filter_options || {
            source: [],
            medium: [],
            campaign: [],
          };
          this.reportQuery = {
            ...query,
            ...(report.metadata.generated_at
              ? { as_of: report.metadata.generated_at }
              : {}),
          };
          this.page = report.pagination?.page || 1;
          this.orderPage = report.order_pagination?.page || 1;
        },
        error: (error) => {
          this.reportQuery = null;
          this.error =
            error?.error?.code === "report_limit"
              ? "O período ultrapassa 10.000 compras. Reduza o período ou refine os filtros. Nenhum total foi truncado."
              : error?.error?.code === "filter_options_limit"
                ? "Há mais de 10.000 opções de divulgação neste recorte. Reduza o período. Nenhuma lista foi truncada."
                : "Não foi possível carregar os resultados de marketing. Confira os filtros e sua permissão e tente novamente.";
        },
      });
  }
  movePage(change: number, granularity: "summary" | "order" = "summary") {
    if (!this.reportQuery || !this.report || this.busy) return;
    const pagination =
      granularity === "order"
        ? this.report.order_pagination
        : this.report.pagination;
    const current = granularity === "order" ? this.orderPage : this.page;
    const next = current + change;
    if (
      !pagination ||
      next < 1 ||
      next > Math.max(1, Math.ceil(pagination.total / pagination.per_page))
    )
      return;
    if (granularity === "order") this.orderPage = next;
    else this.page = next;
    this.loadReport({
      ...this.reportQuery,
      page: String(this.page),
      order_page: String(this.orderPage),
    });
  }
  download(granularity: "summary" | "order" = "summary") {
    if (!this.reportQuery || !this.report || this.busy) return;
    this.requestChanged$.next();
    const eventId = this.eventId;
    this.busy = true;
    this.error = "";
    this.api
      .export(eventId, { ...this.reportQuery, granularity })
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.eventChanged$),
        takeUntil(this.requestChanged$),
        finalize(() => (this.busy = false)),
      )
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = `marketing-evento-${eventId}-${granularity}.csv`;
          anchor.click();
          URL.revokeObjectURL(url);
        },
        error: () => (this.error = "Não foi possível exportar o relatório."),
      });
  }
  private loadIntegrations() {
    this.requestChanged$.next();
    this.busy = true;
    forkJoin({
      integrations: this.api.eventRegistrations(this.eventId),
      bindings: this.api.bindings(this.eventId),
    })
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.eventChanged$),
        takeUntil(this.requestChanged$),
        finalize(() => (this.busy = false)),
      )
      .subscribe({
        next: (result) => {
          this.integrationsLoaded = true;
          this.integrations = result.integrations.integrations.filter((item) =>
            isMarketingProvider(item.provider),
          );
          this.bindings = result.bindings.bindings.filter((binding) =>
            this.integrations.some(
              (item) => item.id === binding.integration_id,
            ),
          );
          this.selected = {};
          this.draftBindings = {};
          for (const provider of this.providers) {
            const bound = this.bindings.find(
              (binding) =>
                this.integrations.find(
                  (integration) => integration.id === binding.integration_id,
                )?.provider === provider.key,
            );
            const integration =
              this.integrations.find(
                (integration) => integration.id === bound?.integration_id,
              ) || this.forProvider(provider.key)[0];
            if (integration)
              this.selectIntegration(provider.key, integration.id);
          }
        },
        error: () =>
          (this.error =
            "Não foi possível carregar as integrações. Confira seu acesso ao evento e tente novamente."),
      });
  }
  forProvider(provider: MarketingProvider) {
    return this.integrations.filter(
      (integration) => integration.provider === provider,
    );
  }
  current(provider: MarketingProvider) {
    return this.integrations.find(
      (integration) => integration.id === Number(this.selected[provider]),
    );
  }
  selectIntegration(
    provider: MarketingProvider,
    integrationId: number | undefined,
  ) {
    if (!isMarketingProvider(provider)) return;
    const integration = this.forProvider(provider).find(
      (item) => item.id === Number(integrationId),
    );
    if (!integration) {
      delete this.selected[provider];
      delete this.draftBindings[provider];
      return;
    }
    this.selected[provider] = integration.id;
    const binding = this.bindings.find(
      (item) => item.integration_id === Number(integrationId),
    );
    this.draftBindings[provider] = binding
      ? this.browserBinding(binding)
      : {
          integration_id: Number(integrationId),
          enabled: false,
          browser_enabled: false,
          allowed_events: ["purchase_confirmed"],
        };
  }
  manage(provider: MarketingProvider) {
    if (!isMarketingProvider(provider) || this.busy || this.readOnly) return;
    this.closeSettings();
    this.managingProvider = provider;
    const ref = this.dialog.open(this.settingsDialog, {
      panelClass: "social-media-dialog",
      width: "600px",
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "90vh",
      ariaLabel: "Configuração da integração " + provider,
    });
    this.settingsDialogRef = ref;
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.settingsDialogRef === ref) {
          this.settingsDialogRef = undefined;
          this.managingProvider = null;
          // Closing without saving never changes the event's persisted channels.
          this.restoreEventSelection(provider);
        }
      });
  }
  editRegistration(
    provider: MarketingProvider,
    integration?: MarketingIntegration,
  ) {
    if (this.busy || this.readOnly || !isMarketingProvider(provider)) return;
    this.closeSettings();
    this.editorDialogRef?.close();
    const ref = this.dialog.open(EventRegistrationEditorComponent, {
      panelClass: "social-media-dialog",
      width: "600px",
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "90vh",
      data: { eventId: this.eventId, provider, integration },
    });
    this.editorDialogRef = ref;
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$), takeUntil(this.eventChanged$))
      .subscribe((record) => {
        if (this.editorDialogRef === ref) this.editorDialogRef = undefined;
        if (record) {
          this.notice =
            "Cadastro salvo somente neste evento. Confira o recebimento na plataforma de anúncios.";
          this.loadIntegrations();
        }
      });
  }
  toggleRegistration(integration: MarketingIntegration) {
    if (this.busy || this.readOnly) return;
    this.busy = true;
    this.error = "";
    let saved = false;
    this.api
      .saveEventRegistration(
        this.eventId,
        {
          enabled: !integration.enabled,
          lock_version: integration.lock_version,
        },
        integration.id,
      )
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.eventChanged$),
        finalize(() => {
          this.busy = false;
          if (saved) this.loadIntegrations();
        }),
      )
      .subscribe({
        next: () => {
          saved = true;
          this.notice = "Cadastro atualizado somente neste evento.";
        },
        error: () =>
          (this.error =
            "Não foi possível atualizar o cadastro. Recarregue e confira seu acesso ao evento."),
      });
  }
  closeSettings() {
    const ref = this.settingsDialogRef;
    const provider = this.managingProvider;
    this.settingsDialogRef = undefined;
    this.managingProvider = null;
    if (provider) this.restoreEventSelection(provider);
    ref?.close();
  }
  private restoreEventSelection(provider: MarketingProvider) {
    const persisted = this.bindings.find((binding) =>
      this.forProvider(provider).some(
        (integration) => integration.id === binding.integration_id,
      ),
    );
    this.selectIntegration(
      provider,
      persisted?.integration_id || this.selected[provider],
    );
  }
  setEventEnabled(provider: MarketingProvider, checked: boolean) {
    const draft = this.draftBindings[provider];
    if (
      !draft ||
      this.busy ||
      this.readOnly ||
      (checked && !this.current(provider)?.enabled)
    )
      return;
    draft.enabled = checked;
    // Selection and activation remain drafts until the producer saves this event.
    this.manage(provider);
  }
  cardStatus(provider: MarketingProvider) {
    const integration = this.current(provider);
    if (!integration) return "Não configurado";
    return integration.enabled ? "Habilitado" : "Desabilitado";
  }
  toggleEvent(provider: MarketingProvider, event: string, checked: boolean) {
    const draft = this.draftBindings[provider];
    if (!draft || this.readOnly) return;
    draft.allowed_events = checked
      ? [...new Set([...draft.allowed_events, event])]
      : draft.allowed_events.filter((item) => item !== event);
  }
  saveBinding(provider: MarketingProvider) {
    if (!isMarketingProvider(provider) || this.readOnly) return;
    const draft = this.draftBindings[provider];
    if (!draft || !this.current(provider) || this.busy) return;
    const other = this.bindings.filter((binding) => {
      const integration = this.integrations.find(
        (item) => item.id === binding.integration_id,
      );
      return (
        integration &&
        isMarketingProvider(integration.provider) &&
        integration.provider !== provider
      );
    });
    const binding = this.browserBinding(draft);
    this.busy = true;
    this.error = "";
    this.api
      .saveEventBindings(this.eventId, [
        ...other.map((item) => this.browserBinding(item)),
        binding,
      ])
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.busy = false)),
      )
      .subscribe({
        next: () => {
          this.notice =
            "Vínculo do evento atualizado. Não haverá envio retroativo de compras antigas.";
          this.loadIntegrations();
        },
        error: () =>
          (this.error =
            "Não foi possível ativar. Valide a integração, habilite o cadastro e confira a versão do vínculo."),
      });
  }
  private browserBinding(binding: MarketingBinding): MarketingBinding {
    // Only active browser fields cross the boundary, including legacy responses.
    return {
      ...(binding.id !== undefined ? { id: binding.id } : {}),
      integration_id: binding.integration_id,
      ...(isMarketingProvider(binding.provider)
        ? { provider: binding.provider }
        : {}),
      enabled: binding.enabled,
      browser_enabled: binding.enabled,
      allowed_events: [...binding.allowed_events],
      ...(binding.configuration_version !== undefined
        ? { configuration_version: binding.configuration_version }
        : {}),
    };
  }
  capability(value: string): string {
    return (
      (
        {
          pending_validation: "Pendente de validação",
          unverified: "Propriedade não verificada",
          browser_ready:
            "Formato válido; recebimento deve ser conferido na plataforma",
          unsupported: "Capacidade indisponível",
          disabled: "Desabilitada",
        } as Record<string, string>
      )[value] ||
      value ||
      "Pendente de validação"
    );
  }
  channel(value: string) {
    return (
      (
        {
          online: "Online",
          pdv_portaria: "PDV Evento",
          pdv_seller: "PDV Yes7",
          pdv_vendedor: "PDV Yes7",
          unknown: "Desconhecido",
        } as Record<string, string>
      )[value] || value
    );
  }
  dimensionOptions(field: "source" | "medium" | "campaign") {
    const values = this.filterOptions[field];
    const selected = this.filters[field];
    return selected && !values.includes(selected)
      ? [...values, selected]
      : values;
  }
  dimensionLabel(
    field: "source" | "medium" | "campaign",
    value: string,
  ): string {
    const labels: Record<string, string> =
      field === "source"
        ? {
            instagram: "Instagram",
            google: "Google",
            facebook: "Facebook",
            whatsapp: "WhatsApp",
            shared_link: "Link compartilhado",
            tiktok: "TikTok",
            direct: "Acesso direto",
            unknown: "Origem não identificada",
          }
        : field === "medium"
          ? {
              paid_social: "Anúncio em rede social (paid_social)",
              cpc: "Anúncio por clique (cpc)",
              organic: "Divulgação orgânica (organic)",
              social: "Rede social (social)",
              messaging: "Mensagens (messaging)",
              sharing: "Compartilhamento (sharing)",
              email: "E-mail (email)",
              referral: "Link de outro site (referral)",
            }
          : {};
    return labels[value] || value;
  }
  get pendingFilters(): boolean {
    if (!this.reportQuery) return false;
    const query = this.query();
    return Object.keys(this.filters).some(
      (key) => (query[key] || "") !== (this.reportQuery?.[key] || ""),
    );
  }
  recordingGapCount(report: MarketingReport): number {
    return Object.values(report.metadata.recording_gaps || {}).reduce(
      (total, count) => total + count,
      0,
    );
  }
}
